import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Select } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import {
  CARTRACK_KEYS,
  type CartrackLinkChange,
  type CartrackVehicle,
  cartrackApi,
  cartrackProblem,
  refusal,
} from '../../services/cartrack.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';

/**
 * 405 (#315, mock-up C4–C6): which Cartrack vehicle this car carries, for administrators only (platform-wide
 * `manage Car`, `holdsAnywhere`): a host's car grant is scoped, passes a type-level check, and must never see the
 * account's other vehicles. Link, change or unlink; the platform refuses a vehicle another car holds and records
 * every change. The plate suggestion is a convenience only: the platform's match (`cartrack/match.ts`) decides.
 */
interface CarLink {
  plate_number?: string;
  cartrack_vehicle_id?: number | null;
  cartrack_linked_at?: string | null;
}

const normalise = (s: string | null | undefined) =>
  (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const describe = (v: CartrackVehicle | undefined, id: number) =>
  v
    ? [v.registration ?? `Vehicle ${id}`, v.vehicle_name, v.make_model].filter(Boolean).join(' · ')
    : `Vehicle ${id}`;
const vehicleLabel = (id: number | null, registration: string | null) =>
  id === null ? 'No vehicle' : registration ? `${registration} (${id})` : `Vehicle ${id}`;

export function CarCartrackCard({
  carId,
  car,
  onChanged,
}: {
  carId: string;
  car: CarLink | undefined;
  onChanged: () => void;
}) {
  const ability = useAbility();
  const mayLink = holdsAnywhere(ability as never, 'manage', 'Car');
  if (!mayLink) return null;
  return <LinkCard carId={carId} car={car} onChanged={onChanged} />;
}

function LinkCard({
  carId,
  car,
  onChanged,
}: {
  carId: string;
  car: CarLink | undefined;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const linkedId = typeof car?.cartrack_vehicle_id === 'number' ? car.cartrack_vehicle_id : null;
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vehicles = useQuery({ queryKey: CARTRACK_KEYS.vehicles, queryFn: cartrackApi.vehicles });
  const changes = useQuery({
    queryKey: CARTRACK_KEYS.changes(carId),
    queryFn: () => cartrackApi.changes(carId),
  });
  const history: CartrackLinkChange[] = changes.data?.items ?? [];
  const items = vehicles.data?.items ?? [];
  const suggested = items.find(
    (v) =>
      !v.car &&
      normalise(v.registration) !== '' &&
      normalise(v.registration) === normalise(car?.plate_number),
  );

  // Preselect the vehicle whose registration matches the plate, once the list arrives.
  useEffect(() => {
    if (!chosen && suggested) setChosen(String(suggested.vehicle_id));
  }, [chosen, suggested]);

  const save = useMutation({
    mutationFn: (vehicleId: number | null) => cartrackApi.setCarVehicle(carId, vehicleId),
    onSuccess: () => {
      setError(null);
      setChoosing(false);
      setUnlinking(false);
      queryClient.invalidateQueries({ queryKey: ['cartrack'] });
      queryClient.invalidateQueries({ queryKey: CARTRACK_KEYS.changes(carId) });
      queryClient.invalidateQueries({ queryKey: CARTRACK_KEYS.position(carId) });
      onChanged();
    },
    onError: (err) => {
      setUnlinking(false);
      const refused = refusal(err);
      if (refused?.code === 'CARTRACK_VEHICLE_TAKEN') {
        const plate = refused.details.plate_number;
        setError(
          plate
            ? `That vehicle is linked to ${String(plate)}. Unlink it there first.`
            : 'That vehicle is linked to another car. Unlink it there first.',
        );
        return;
      }
      setError(
        cartrackProblem(err)?.text ?? (err instanceof Error ? err.message : 'Could not save.'),
      );
    },
  });

  const latest = history[0];
  const picker = (
    <div className="mt-3">
      {vehicles.isError ? (
        <p
          data-testid="cartrack-problem"
          className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          {cartrackProblem(vehicles.error)?.text ?? 'Could not load the Cartrack vehicles.'}
        </p>
      ) : (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <FormField label="Cartrack vehicle" name="cartrack_vehicle">
              <Select
                id="cartrack_vehicle"
                name="cartrack_vehicle"
                value={chosen}
                onChange={(e) => setChosen(e.target.value)}
              >
                <option value="">Choose a Cartrack vehicle</option>
                {items.map((v) => {
                  const other = v.car && v.vehicle_id !== linkedId ? v.car : null;
                  return (
                    <option
                      key={v.vehicle_id}
                      value={String(v.vehicle_id)}
                      disabled={Boolean(other)}
                    >
                      {describe(v, v.vehicle_id)}
                      {v === suggested ? ' (matches this plate)' : ''}
                      {other ? ` (linked to ${other.plate_number})` : ''}
                    </option>
                  );
                })}
              </Select>
            </FormField>
          </div>
          <Button
            type="button"
            disabled={!chosen || Number(chosen) === linkedId}
            loading={save.isPending}
            onClick={() => save.mutate(Number(chosen))}
          >
            Link
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Cartrack</h2>
        {linkedId === null && (
          <span className="rounded bg-surface px-2 py-0.5 text-xs font-semibold text-steel">
            Not on Cartrack
          </span>
        )}
      </div>

      {linkedId !== null ? (
        <>
          <dl
            data-testid="cartrack-link"
            className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1 text-sm"
          >
            <dt className="text-steel">Vehicle</dt>
            <dd className="font-semibold">
              {describe(
                items.find((v) => v.vehicle_id === linkedId),
                linkedId,
              )}
            </dd>
            {car?.cartrack_linked_at && (
              <>
                <dt className="text-steel">Linked</dt>
                <dd>
                  {mytDayLabel(car.cartrack_linked_at)}, {mytClock(car.cartrack_linked_at)}
                  {latest?.to_vehicle_id === linkedId && latest.by.name
                    ? ` by ${latest.by.name} (${latest.cause === 'match' ? 'plate match' : 'by hand'})`
                    : ''}
                </dd>
              </>
            )}
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" onClick={() => setChoosing((v) => !v)}>
              Change vehicle
            </Button>
            <Button type="button" variant="danger" onClick={() => setUnlinking(true)}>
              Unlink
            </Button>
          </div>
          {choosing && picker}
        </>
      ) : (
        picker
      )}

      {error && (
        <p
          data-testid="cartrack-error"
          className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </p>
      )}

      {history.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => setShowHistory((v) => !v)}
          >
            History ({history.length})
          </button>
          {showHistory && (
            <ul data-testid="cartrack-history" className="mt-2 space-y-1 text-sm text-ink">
              {history.map((h) => (
                <li key={`${h.at}:${h.to_vehicle_id}`}>
                  {vehicleLabel(h.from_vehicle_id, null)} →{' '}
                  {vehicleLabel(h.to_vehicle_id, h.registration)}, {mytDayLabel(h.at)}{' '}
                  {mytClock(h.at)}
                  {h.by.name ? `, by ${h.by.name}` : ''}
                  {h.cause === 'match' ? ' (plate match)' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Modal
        open={unlinking}
        onClose={() => setUnlinking(false)}
        title={`Unlink ${car?.plate_number ?? 'this car'} from Cartrack?`}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setUnlinking(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Unlink
            </Button>
          </>
        }
      >
        <p className="text-sm text-steel">
          Customers and staff will no longer see its live position. Its GPS tracker link, if any, is
          shown again.
        </p>
      </Modal>
    </Card>
  );
}
